import { action, computed, flow, makeObservable, observable } from "mobx";
import { API, Auth } from "aws-amplify";
import { getCustomers, getUsers, getPermissionCatalogUser } from "../../graphql/queries";
import { tokenService } from "../../services/tokenService";
import { getAgentList } from "../../services/agents";

const PermissionLevelsMapping = {
  v: 'VIEW',
  u: 'UPDATE',
  c: 'CREATE',
  d: 'DELETE'
}

// Helper function to decode JWT token
const decodeJWT = (token) => {
  try {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

class AuthStore {
  isLoading = true;
  isError = false;
  isSuperAdmin = false;
  isGroupSuperAdmin = false;
  isGroupAdmin = false;
  isCustomerAdmin = false;
  isUser = false;
  users = [];
  customerId = "";
  userId = "";  // Cognito sub (legacy)
  apiUserId = "";  // user_uuid del token JWT
  currentWorkspaceUuid = null;  // tenant_uuid del workspace actual (sincronizado con token)
  customer = null;
  selectedAccount = null;
  favoriteAccount = [];
  listAccount = [];
  agents = [];
  userData = null;
  currentUserFirstName = "";
  userPermissions = [];
  navbarActionElement = null;

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      isError: observable,
      isSuperAdmin: observable,
      isCustomerAdmin: observable,
      isGroupSuperAdmin: observable,
      isGroupAdmin: observable,
      isUser: observable,
      customerId: observable,
      customer: observable,
      users: observable,
      userId: observable,
      apiUserId: observable,
      currentWorkspaceUuid: observable,
      listAccount: observable,
      selectedAccount: observable,
      favoriteAccount: observable,
      agents: observable,
      userData: observable,
      currentUserFirstName: observable,
      navbarActionElement: observable,
      selectedAccountInitials: computed,
      sortedAccountListByFavs: computed,
      clear: action,
      pickAccount: action,
      rollbackAccount: action,
      saveLocalStorageFavoriteAccounts: action,
      getGlobalInformation: flow,
      fetchAgents: flow,
      fetchUserData: flow,
      fetchUserPermissions: flow,
      userPermissions: observable,
      hasPermission: action,
      setNavbarActionElement: action,
      fetchAllUsers: flow,
      fetchApiToken: flow,
      refreshTokenForWorkspace: flow,
      syncWorkspaceUuidFromToken: action,
      syncWorkspaceUuidFromWorkspace: action
    });

  }

  // eslint-disable-next-line getter-return
  get selectedAccountInitials() {
    if (this.selectedAccount) {
      const splitName = this.selectedAccount.name.split(" ").slice(0, 2);
      let initials = "";

      if (splitName.length === 1) {
        initials = splitName[0].slice(0, 2);
      } else {
        initials = splitName.map((word) => word.charAt(0)).join("");
      }

      return initials.toUpperCase();
    }
  }

  pickAccount(id) {
    const account = this.listAccount.find((account) => account.id === id);
    this.saveLocalStorageFavoriteAccounts(account.id);
    this.selectedAccount = account;
  }

  rollbackAccount() {
    if (this.favoriteAccount.length > 0) {
      this.pickAccount(this.favoriteAccount[1]);
    }
  }

  saveLocalStorageFavoriteAccounts(id) {
    const currentFavoritesAccount =
      JSON.parse(localStorage.getItem("favoriteAccount")) || [];
    const existId = currentFavoritesAccount.findIndex(
      (idSaved) => idSaved.trim() === id.trim(),
    );

    if (existId !== -1) {
      currentFavoritesAccount.splice(existId, 1);
    }

    currentFavoritesAccount.unshift(id);

    if (currentFavoritesAccount.length > 5) {
      currentFavoritesAccount.pop();
    }

    this.favoriteAccount = currentFavoritesAccount;
    localStorage.setItem(
      "favoriteAccount",
      JSON.stringify(currentFavoritesAccount),
    );
  }

  get sortedAccountListByFavs() {
    const indexMap = {};
    const currentFavoritesAccount = [...this.favoriteAccount];
    const currentAccounts = [...this.listAccount];

    currentFavoritesAccount.forEach((favorite, index) => {
      indexMap[favorite] = index;
    });

    return currentAccounts.sort((a, b) => {
      const indexA = indexMap[a.id];
      const indexB = indexMap[b.id];

      if (indexA !== undefined && indexB !== undefined) {
        return indexA - indexB;
      }

      if (indexA !== undefined) {
        return -1;
      }
      if (indexB !== undefined) {
        return 1;
      }

      return 0;
    });
  }

  clear() {
    localStorage.removeItem("favoriteAccount");
    localStorage.removeItem("userId");
    localStorage.removeItem("api_access_token");
    localStorage.removeItem("user_uuid");
    localStorage.removeItem("workspace_uuid");
    this.isLoading = true;
    this.isError = false;
    this.isSuperAdmin = false;
    this.isCustomerAdmin = false;
    this.isUser = false;
    this.customerId = "";
    this.userId = "";
    this.apiUserId = "";
    this.currentWorkspaceUuid = null;
    this.customer = null;
    this.selectedAccount = null;
    this.listAccount = [];
    this.agents = [];
    this.userData = null;
    this.currentUserFirstName = "";
    this.userPermissions = [];
  }


  *getGlobalInformation() {

    try {
      this.isLoading = true;
      const userInfo = yield Auth.currentAuthenticatedUser({
        bypassCache: true,
      });

      const groups =
        userInfo.signInUserSession.accessToken.payload["cognito:groups"];

      const responseCustomer = yield API.graphql({
        query: getCustomers,
        variables: {
          input: { id: userInfo.attributes["custom:customer"] ?? "" },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      const customerData = JSON.parse(
        responseCustomer?.data?.getCustomers?.body,
      );

      const response = yield API.graphql({
        query: getCustomers,
        variables: {
          input: { id: "" },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      const customerList = JSON.parse(response.data?.getCustomers?.body).map(
        ({ name, id, metadata, ...rest }) => ({ name, id, metadata, ...rest }),
      );

      const favoritesAccount =
        JSON.parse(localStorage.getItem("favoriteAccount")) || [];

      if (favoritesAccount.length === 0) {
        this.saveLocalStorageFavoriteAccounts(customerData?.id);
        favoritesAccount.unshift(customerData?.id);
      }

      this.isSuperAdmin = groups?.includes("superadmin");
      this.isCustomerAdmin = groups?.includes("customeradmin");
      this.isUser = groups?.includes("user");
      this.customerId = userInfo.attributes["custom:customer"] ?? "";
      this.userId = userInfo.attributes.sub ?? "";
      this.customer = customerData;
      this.selectedAccount = customerList.find(
        (account) => account.id === favoritesAccount[0],
      );
      this.listAccount = [...customerList];
      this.favoriteAccount = favoritesAccount;
      yield this.fetchUserData(this.userId);
      yield this.fetchUserPermissions();
      yield this.fetchApiToken();

    } catch (error) {
      this.clear();
    } finally {
      this.isLoading = false;
    }
  }

   *fetchApiToken() {
    console.log('fetchApiToken ###3####3#########');
     try {
      // Prioridad 1: Usar workspace_uuid de localStorage (workspace seleccionado por usuario)
      // Prioridad 2: Usar variable de entorno (workspace por defecto)
      const storedWorkspaceUuid = localStorage.getItem("workspace_uuid");
      const storedUserUuid = localStorage.getItem("user_uuid");
      
      const userUuid = storedUserUuid || process.env.REACT_APP_USER_UUID;
      const tenantUuid = storedWorkspaceUuid || process.env.REACT_APP_TENANT_UUID;
      
      if (!userUuid || !tenantUuid) {
        console.error('Could not get user_uuid or tenant_uuid');
        return;
      }
      
      // Si usamos valores de localStorage, log para depuración
      if (storedWorkspaceUuid) {
        console.log('🔄 Usando workspace seleccionado de localStorage:', storedWorkspaceUuid);
      } else {
        console.log('🔄 Usando workspace por defecto de variables de entorno:', tenantUuid);
      }
      
      console.log('userUuid', userUuid);
      console.log('tenantUuid', tenantUuid);
      console.log('fetchApiToken ###3####3#########');
      
      const token = yield tokenService.fetchNewToken(userUuid, tenantUuid);
      
      // Decodificar el token y extraer user_uuid y tenant_uuid
      if (token) {
        const decodedToken = decodeJWT(token);
        if (decodedToken?.user_uuid) {
          this.apiUserId = decodedToken.user_uuid;
          console.log('✅ API User ID extraído del token:', this.apiUserId);
        } else {
          console.warn('⚠️ No se pudo extraer user_uuid del token');
        }
        
        // Sincronizar tenant_uuid desde el token
        this.syncWorkspaceUuidFromToken(token);
      }
      
      // Guardar en localStorage para interceptores
      localStorage.setItem("user_uuid", this.apiUserId);

     } catch (err) {
      console.error("Error obteniendo token API:", err);
    }
  }

  /**
   * Sincroniza currentWorkspaceUuid desde el token decodificado
   * @param {string} token - JWT token
   */
  syncWorkspaceUuidFromToken(token) {
    const decodedToken = decodeJWT(token);
    if (decodedToken?.tenant_uuid) {
      this.currentWorkspaceUuid = decodedToken.tenant_uuid;
      localStorage.setItem("workspace_uuid", this.currentWorkspaceUuid);
      console.log('✅ Workspace UUID sincronizado desde token:', this.currentWorkspaceUuid);
    } else {
      console.warn('⚠️ No se pudo extraer tenant_uuid del token');
    }
  }

  /**
   * Sincroniza currentWorkspaceUuid desde WorkspaceStore (cuando cambia manualmente)
   * @param {string} workspaceUuid - Workspace UUID
   */
  syncWorkspaceUuidFromWorkspace(workspaceUuid) {
    if (workspaceUuid) {
      this.currentWorkspaceUuid = workspaceUuid;
      localStorage.setItem("workspace_uuid", workspaceUuid);
      console.log('✅ Workspace UUID sincronizado desde workspace:', workspaceUuid);
    }
  }

  /**
   * Refresca el token cuando cambia el workspace
   * @param {string} tenantUuid - Nuevo tenant UUID
   * @returns {Promise<void>}
   */
  *refreshTokenForWorkspace(tenantUuid) {
    try {
      if (!this.apiUserId) {
        console.error('⚠️ No hay apiUserId disponible para refrescar token');
        return;
      }
      
      console.log('🔄 Refrescando token para nuevo workspace:', tenantUuid);
      const token = yield tokenService.refreshToken(this.apiUserId, tenantUuid);
      
      // Sincronizar el nuevo tenant_uuid
      this.syncWorkspaceUuidFromToken(token);
      
      console.log('✅ Token refrescado exitosamente para nuevo workspace');
    } catch (err) {
      console.error("Error refrescando token para workspace:", err);
      throw err;
    }
  }

  *fetchAgents() {
    try {
      this.isError = false;
      const response = yield getAgentList();
      const agents = response.data?.agents || [];
      
      // Map the new agent structure to match expected format
      // uuid from new API maps to both uuid and id for compatibility
      this.agents = agents.map(agent => {
        const starters = (Array.isArray(agent.starters) && agent.starters.length > 0)
          ? agent.starters
          : (Array.isArray(agent.metadata?.conversation_starters) && agent.metadata.conversation_starters.length > 0)
            ? agent.metadata.conversation_starters
            : [];
        
        return {
          ...agent,
          id: agent.uuid, // Add id field for backward compatibility
          photo_url: agent.metadata?.imageUrl || agent.image || '',
          conversation_starters: starters
        };
      });
    } catch (error) {
      console.error('Error fetching agents:', error);
      this.isError = true;
      this.agents = [];
    }
  }

  *fetchAllUsers() {
    this.isError = false;
    try {
      // GraphQL removed - commented out
      // const response = yield API.graphql({
      //   query: getUsers,
      //   variables: { input: { id: "", customer: this.customerId } },
      //   authMode: "AMAZON_COGNITO_USER_POOLS",
      // });
      // const users = JSON.parse(response.data?.getUsers?.body);
      // this.users = users;
      // return users;
      // TODO: Replace with REST API call
      this.users = [];
      return [];
    } catch (error) {
      this.isError = true;
    }
  }

  *fetchUserData(userId) {
    try {
      const response = yield API.graphql({
        query: getUsers,
        variables: { input: { id: userId, customer: this.customerId } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const userData = JSON.parse(response.data?.getUsers?.body);
      this.userData = userData;
      this.currentUserFirstName = userData.first_name;
      this.isGroupSuperAdmin = userData.is_super_admin;
      this.isGroupAdmin = userData.is_admin;
    } catch (error) {
      this.isError = true;
    }
  }

  *fetchUserPermissions() {
    try {
      this.isLoading = true;
      const response = yield API.graphql({
        query: getPermissionCatalogUser,
        variables: {
          input: {
            customer_id: this.selectedAccount.id,
            user_id: this.userId
          }
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      });

      this.userPermissions = JSON.parse(response.data?.getPermissionCatalogUser?.body) || [];
    } catch (error) {
      this.userPermissions = [];
    }
  }


  hasPermission(code, action = 'v') {
    if (this.isGroupSuperAdmin || this.isGroupAdmin) {
      return true;
    }
    const permissionObject = this.userPermissions.find(p => p.code === code);
    return  permissionObject && permissionObject.permission[PermissionLevelsMapping[action]] !== 'none'
  }

  setNavbarActionElement(element) {
    this.navbarActionElement = element;
  }
}

export default AuthStore;